<template>
  <form class="form" novalidate @submit.prevent="handleFormSubmit" @keyup.enter="handleFormSubmit">
    <ion-card class="form-block">
      <div class="form-block-logo">
        <app-logo :show-logo-only="false" />
      </div>

      <ion-segment v-if="!isCustomDomain()" :value="loginType" @ion-change="handleLoginChange($event)">
        <ion-segment-button :value="LoginTypeEnum.Form">
          <ion-label>{{ $t('auth.logIn.formTab') }}</ion-label>
        </ion-segment-button>
        <ion-segment-button :value="LoginTypeEnum.Uri">
          <ion-label>{{ $t('auth.logIn.uriTab') }}</ion-label>
        </ion-segment-button>
      </ion-segment>

      <form-input
        v-if="showUriField"
        v-model:value="uriModel"
        type="text"
        :placeholder="$t('auth.logIn.form.uri')"
        :required="true"
        :spellcheck="false"
        autocapitalize="off"
        :disabled="isSettingsLoading"
      >
        <div v-if="!(form.uri && form.uri.startsWith('http'))" slot="start" class="protocol" v-html="'https://'" />
      </form-input>

      <not-found-data v-if="showNetworkNotFoundError" :text="$t('auth.logIn.networkNotFound')" />

      <div v-if="showUriErrors">
        <form-error name="fullURL" :errors-array="fullURLErrors" />
        <form-error name="uri" :errors-array="uriErrors" />
      </div>

      <form-input
        v-if="showForm"
        v-model:value="form.login"
        :placeholder="$t('auth.logIn.form.login')"
        autocapitalize="off"
        autocomplete="username"
        :spellcheck="false"
      />

      <div v-if="showLoginErrors">
        <form-error name="login" :errors-array="loginErrors" />
      </div>

      <form-input
        v-if="showForm"
        v-model:value="form.password"
        :type="showPassword ? 'text' : 'password'"
        :placeholder="$t('auth.logIn.form.password')"
        autocomplete="current-password"
      >
        <icons-provider
          class="icon"
          :icon-props="{
            width: '20',
            height: '20',
            fill: 'var(--ion-color-medium)',
          }"
          :name="showPassword ? AppIconsEnum.Eye : AppIconsEnum.EyeSlash"
          @click="showPassword = !showPassword"
        />
      </form-input>

      <div v-if="showPasswordErrors">
        <form-error name="password" :errors-array="passwordErrors" />
      </div>

      <service-selector v-if="showServiceSelector" />

      <ion-button
        mode="md"
        fill="solid"
        expand="block"
        type="submit"
        size="default"
        class="form-block-submit_button"
        color="intra"
        :disabled="isLoginButtonDisabled"
        :class="!appStore.isDarkTheme(appStore.theme) ? 'light' : 'dark'"
      >
        <ion-label color="custom">{{ isLoading ? $t('auth.logIn.loading') : $t('auth.logIn.title') }}</ion-label>
      </ion-button>

      <login-bottom-section :page-ref="pageRef" />
    </ion-card>
  </form>
</template>

<script lang="ts" setup>
import type { SegmentCustomEvent } from '@ionic/vue';
import { IonButton, IonCard, IonLabel, IonSegment, IonSegmentButton, modalController } from '@ionic/vue';
import type { ErrorObject } from '@vuelidate/core';
import useVuelidate from '@vuelidate/core';
import { watchDebounced, useWindowSize } from '@vueuse/core';
import type { ComponentPublicInstance, ComputedRef, PropType } from 'vue';
import { computed, onBeforeMount, onMounted, onUnmounted, reactive, ref } from 'vue';
import { useRouter } from 'vue-router';

import {
  AppLogo,
  FormError,
  FormInput,
  ServiceSelector,
  IconsProvider,
  LoginBottomSection,
  NotFoundData,
} from '@/components';
import { AppIconsEnum } from '@/enums';
import {
  useTheme,
  useSession,
  isNativeMobile,
  required,
  useLayout,
  normalizeDomain,
  domain,
  domainRegex,
} from '@/helpers';
import { useOAuth } from '@/helpers/useOAuthHelper';
import { useAutoLogin } from '@/helpers/useAutoLoginHelper';
import { resetAllStores, StoresEnum, useAppStore, useAuthStore, useNetworkStore, useUserStore } from '@/store';
import type { NetworkLocateModel, AuthFormModel } from '@/types';
import { getFullUrl } from '@/services/getters/getFullUrl';
import { isCustomDomain } from '@/services/checkers';
import { useI18n } from '@/i18n';
import { LoginTypeEnum, UserVerificationTypeEnum } from '@/enums/auth';
import { ROUTES_NAME } from '@/router';
import { getServiceHost } from '@/services/getters/getServiceHost';

const props = defineProps({
  pageRef: {
    type: [Object, null] as PropType<ComponentPublicInstance | null>,
    default: null,
  },
  isModal: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['loading-settings']);

//#region Variables
const router = useRouter();
const { t } = useI18n();
const { width: innerWidth } = useWindowSize();
const { webPopupOAuth, nativePopupOAuth } = useOAuth();
const { enabled: isAutoLoginEnabled, attempt: attemptAutoLogin } = useAutoLogin();

const appStore = useAppStore();
const authStore = useAuthStore();
const networkStore = useNetworkStore();
const userStore = useUserStore();

const form: AuthFormModel = reactive({
  login: '',
  password: '',
  uri: '',
  fullURL: '',
});

const isSettingsLoading = ref<boolean>(false);
const showPassword = ref<boolean>(false);
const isOauth = ref<boolean>(false);
const isAutoLocate = ref<boolean>(false);
const loginType = ref<LoginTypeEnum>(LoginTypeEnum.Form);
const locateSettings = ref<NetworkLocateModel | undefined>(undefined);

const uriError = ref<string | null>(null);
const loginError = ref<string | null>(null);
const passwordError = ref<string | null>(null);
const otherError = ref<string | null>(null);

const showServiceSelector = computed(() => __DEV__ && showForm.value);
const isUri = computed(() => loginType.value === LoginTypeEnum.Uri);
const showUriField = computed(() => !isCustomDomain() && isUri.value);
const showForm = computed(
  () => loginType.value === LoginTypeEnum.Form || (form.uri && locateSettings.value && !isOauth.value)
);
const isLoading = computed(() => appStore.isLoading || networkStore.isLoading || userStore.isLoading);
const isLoginButtonDisabled = computed(
  () => isLoading.value || (loginType.value === LoginTypeEnum.Uri && !locateSettings.value)
);

// Show errors
const showNetworkNotFoundError = computed(() => isCustomDomain() && isUri.value && uriError.value);
const showUriErrors = computed(
  () => showUriField.value && (fullURLErrors.value.length > 0 || uriErrors.value.length > 0)
);
const showLoginErrors = computed(() => !isUri.value && loginErrors.value.length > 0);
const showPasswordErrors = computed(() => !isUri.value && passwordErrors.value.length > 0);

// Validation rules
const validationRules = computed(() => {
  if (isUri.value) {
    return {
      login: { required },
      password: { required },
      uri: { required, domain },
    };
  }

  return {
    login: { required },
    password: { required },
  };
});

const v$ = useVuelidate(validationRules, form);

// Error computeds
const loginErrors: ComputedRef<string[]> = computed(() => {
  const validationErrors = v$.value.login.$errors.map((m: ErrorObject) => m.$message as string);
  return loginError.value ? [...validationErrors, loginError.value] : validationErrors;
});

const passwordErrors: ComputedRef<string[]> = computed(() => {
  const validationErrors = v$.value.password.$errors.map((m: ErrorObject) => m.$message as string);
  return passwordError.value ? [...validationErrors, passwordError.value] : validationErrors;
});

const fullURLErrors: ComputedRef<string[]> = computed(() => {
  if (!v$.value?.fullURL) return [];
  return v$.value.fullURL.$errors.map((m: ErrorObject) => m.$message as string);
});

const uriErrors: ComputedRef<string[]> = computed(() => {
  return uriError.value ? [uriError.value] : [];
});

const uriModel = computed({
  get: () => form.uri,
  set: (val: string) => {
    form.uri = normalizeDomain(val);
  },
});
//#endregion

//#region Methods
function clearErrors() {
  uriError.value = null;
  loginError.value = null;
  passwordError.value = null;
  otherError.value = null;
}

function clearForm() {
  form.login = '';
  form.password = '';
  form.uri = '';
  form.fullURL = '';
  clearErrors();
  v$.value.$reset();
}

function triggerSettingsLoading(bool: boolean) {
  isSettingsLoading.value = bool;
  emit('loading-settings', bool);
}

async function checkSettings(): Promise<void> {
  await v$.value.$validate();
  if (fullURLErrors.value.length !== 0) {
    await clearSettings();
    return;
  }

  /**
   * @note Covers cases:
   * - 1. G + F + R => no change
   * - 5. C + (F / E / X) + NO R => no change
   * @see src/helpers/useRedirectHelper.ts
   */
  if (!isCustomDomain() && form.uri) {
    authStore.setHost(form.uri);
  }

  triggerSettingsLoading(true);

  try {
    locateSettings.value = await networkStore.getLocate(form.fullURL);
    if (!locateSettings.value) {
      console.warn('[WARN] Settings for that network uri not found');
      uriError.value = t('auth.logIn.networkNotFound');
      await clearSettings();
      return;
    }
    isOauth.value = locateSettings.value.verificationType === UserVerificationTypeEnum.VerificationByOauth;
    const normalizedHost = locateSettings.value.hostWithSubHost
      .replace(/^https?:\/\//, '')
      .replace('core-beta2', 'core');

    await useTheme().setTheme(useTheme().getAppColor(locateSettings.value.headBackgroundColor));

    /**
     * @note On Generic domain if checkSettings() is called
     * that means we are in Enterprise flow, thus we need to set host
     * using legacy `hostWithSubHost`
     * @note On both Generic and Custom
     * we need to set host as OAuth, to support MVC external auth
     *
     * @note Covers cases:
     * - 3. G + E => hostWithSubHost
     * - 4. G + X => hostWithSubHost
     * @see src/helpers/useRedirectHelper.ts
     */
    if (!isCustomDomain() || isOauth.value) {
      authStore.setHost(normalizedHost);
    }

    clearErrors();
    triggerSettingsLoading(false);
    v$.value.$reset();

    // If OAuth, trigger submit
    if (isOauth.value) {
      await onSubmit();
    }
  } catch (error) {
    console.error('[ERROR] Failed to get network settings:', error);
    uriError.value = t('auth.logIn.networkNotFound');
    await clearSettings();
  } finally {
    triggerSettingsLoading(false);
  }
}

async function clearSettings() {
  console.debug('[DEBUG] Clearing settings');
  if (loginType.value === LoginTypeEnum.Uri) locateSettings.value = undefined;

  appStore.$reset();
  authStore.$reset();
  networkStore.$reset();
  userStore.$reset();

  isOauth.value = false;
  isAutoLocate.value = false;

  await useTheme().clearTheme();
}

async function handleLoginChange(ev: SegmentCustomEvent) {
  const type = ev.detail.value;
  if (!type) return console.warn(`[WARN] Login change: type is null`);

  loginType.value = type as LoginTypeEnum;
  clearForm();
  await clearSettings();
}

async function handleNetworkSelect(form: AuthFormModel) {
  const networks = await networkStore.networksByForm(form);
  if (!networks?.length) {
    console.warn('[WARN] Failed to get networks');
    loginError.value = 'Invalid credentials';
    return;
  }
  clearErrors();

  /** @see docs/AUTH_MATRIX.md */
  if (!locateSettings.value) {
    authStore.setAuthType('Form');
  } else if (locateSettings.value.verificationType === UserVerificationTypeEnum.VerificationByOauth) {
    authStore.setAuthType('External');
  } else {
    authStore.setAuthType('Enterprise');
  }

  await useSession().changeNetwork(networks, false, form);
}

async function handleFormSubmit() {
  loginType.value === LoginTypeEnum.Uri && !locateSettings.value
    ? console.warn('[WARN] Nope - do not see network settings')
    : onSubmit();
}

async function handleModalSubmit() {
  const isValidate = (isOauth.value && isUri.value) || (await v$.value.$validate());
  if (!isValidate) return;

  await router.push({ name: ROUTES_NAME.LOADING });
  await modalController.dismiss();
  resetAllStores([StoresEnum.APP]);
  authStore.setHost(getServiceHost());

  try {
    await handleNetworkSelect(form);
  } catch (e) {
    console.error('[ERROR] Modal authentication error:', e);
  } finally {
    await router.push({ name: ROUTES_NAME.HOME });
  }
}

async function handlePageSubmit() {
  const isValidate = (isOauth.value && isUri.value) || (await v$.value.$validate());
  if (!isValidate || props.pageRef === null) return;

  appStore.$reset();
  networkStore.$reset();

  try {
    /** @note Handle OAuth authentication */
    if (isOauth.value && isUri.value) {
      const link = await networkStore.networksByUri();

      if (!link) {
        console.warn('[WARN] Failed to get link');
        return;
      }

      isNativeMobile ? nativePopupOAuth(link) : webPopupOAuth(link);
      return;
    }

    /** @note Handle Form-based authentication */
    await handleNetworkSelect(form);
  } catch (e) {
    console.error('[ERROR] Authentication error:', e);
  }
}

async function onSubmit() {
  props.isModal ? await handleModalSubmit() : await handlePageSubmit();
}
//#endregion

//#region Watchers
watchDebounced(
  () => ({ ...form }),
  async (newValue, oldValue) => {
    if (newValue.uri === oldValue.uri) {
      console.log('[INFO] Form watcher: uri is the same');
      return;
    }

    if (!form.uri) {
      console.log('[INFO] Form watcher: uri is empty. Clearing settings');
      await clearSettings();
      return;
    }

    if (!isUri.value) {
      console.log('[INFO] Form watcher: not uri mode');
      return;
    }

    const originalUri = form.uri;
    form.uri = normalizeDomain(form.uri);
    console.log('[DEBUG] URI validation:', {
      original: originalUri,
      normalized: form.uri,
      domainRegexTest: domainRegex.test(form.uri),
    });

    if (!domainRegex.test(form.uri)) {
      console.log('[DEBUG] Failed domain regex test');
      uriError.value = t('auth.logIn.invalidDomain');
      return;
    }

    const fullURL = getFullUrl(form.uri);
    console.log('[DEBUG] Full URL result:', fullURL);
    if (!fullURL) {
      uriError.value = t('auth.logIn.invalidDomain');
      return;
    }
    form.fullURL = fullURL;
    await checkSettings();
  },
  { debounce: 400, maxWait: 10000 }
);
//#endregion

//#region Lifecycle
onBeforeMount(async () => {
  showPassword.value = false;
  clearForm();
});

onMounted(async () => {
  // If user is already authenticated AND we are NOT in modal mode
  // redirect him to home page. Otherwise (modal mode), stay here to allow user change.
  const goHome = authStore.isAuth() && !props.isModal;
  if (goHome) {
    console.log('[INFO] User is already logged in (page mode). Redirecting to home page');
    await router.replace(appStore.homePage);
    return;
  }

  // If user is NOT authenticated or IS in modal mode
  // we need to clear settings
  // to eliminate any conflicts with auth data
  if (!authStore.isAuth()) await clearSettings();

  // Next we need to check if auto login is enabled (only in page mode)
  // Right now it is only enabled for Klinikum Erding
  const autoLoginCase = !props.isModal && (await isAutoLoginEnabled());
  if (autoLoginCase) {
    const loggedIn = await attemptAutoLogin();
    if (loggedIn) {
      console.log('[INFO] Auto login was successful');
      return;
    }
    console.warn('[WARN] Auto login was not successful. Falling back to normal login');
  }

  // Finally we need to check if user is on custom domain.
  // If yes - we need to update form and check settings here
  // Important: We need to block watchDebounced() above
  // so it doesn't trigger twice, thus isAutoLocate
  // And don't forget to 'click' submit button for users on mobile.
  // We need that for network with oauth to open ip-app-browser
  // right after checkSettings() returns verificationType - VerificationByOauth
  if (isCustomDomain()) {
    isAutoLocate.value = true;
    loginType.value = LoginTypeEnum.Uri;
    form.uri = authStore.host;
    const fullURL = getFullUrl(authStore.host);
    if (!fullURL) {
      uriError.value = t('auth.logIn.invalidDomain');
      return;
    }
    form.fullURL = fullURL;
    await checkSettings();
  }
});

onUnmounted(() => {
  useLayout().updateInnerWidth(innerWidth.value);
});
//#endregion
</script>

<style scoped lang="scss">
.app-grid {
  height: 100%;

  &.grid-fixed {
    --ion-grid-padding: 0;
    max-width: app-bp(md);
  }

  .app-col {
    --ion-grid-column-padding: 0;
  }

  .app-row {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.form-block {
  padding: app-padding(lg);
  border-radius: #{app-radius(md)};

  ion-item .icon {
    margin-left: #{app-padding(md)};
    &:hover {
      cursor: pointer;
    }
  }

  ion-button {
    margin-top: 1rem;

    &.form-block-submit_button {
      --box-shadow: none;
      --border-radius: #{app-radius(md)};
      --border-style: solid;
      --border-width: 1px;
      margin: 0;
      margin-top: app-padding(lg);
      white-space: normal;
      color: var(--ion-color-custom);

      &:active::part(native) {
        background: var(--ion-color-intra-activated);
      }

      &.dark {
        --border-color: var(--ion-card-background);
      }

      &.light {
        --border-color: var(--ion-color-light-tint);
      }

      ion-label {
        margin: app-padding(md);
        font-weight: bold;
      }
    }
  }

  &-logo {
    margin-bottom: app-padding(lg);
    margin-top: app-padding(lg);
    display: flex;
    justify-content: center;
    align-items: center;
    svg {
      width: 50%;
    }
  }

  .protocol {
    font-size: 1.1rem;
    margin-right: 0;
  }
}

ion-segment-button {
  --color-checked: var(--ion-color-medium);
  --color-hover: var(--ion-color-medium);
  max-width: 100%;
}
</style>
